import { JsonSafe } from 'utils/JsonSafe';

import Emitter from 'Emitter';

import styles from './DmrFrame.css';

type Settings = {
    className: string;
    el: Node;
};
export class DmrFrame extends Emitter {
    static FRAME_FIMEOUT = 60000;

    static EVENT_LOADED = 'loaded';
    static EVENT_LOADING = 'loading';
    static EVENT_LOAD_ERROR = 'load-error';
    static EVENT_LOAD_ERROR_TIMEOUT = 'load-error-timeout';
    static EVENT_RETRY = 'retry';
    static EVENT_PAYMENT_SUCCESS = 'payment-success';
    static EVENT_PAYMENT_CANCEL = 'payment-cancel';
    static EVENT_PAYMENT_WAITING = 'payment-waiting';
    static EVENT_PAYMENT_FAIL = 'payment-fail';
    static EVENT_CLOSE = 'close';
    static EVENT_MESSAGE = 'message';

    _el: Node;
    _className: string;
    _frame?: HTMLIFrameElement;
    _loaded = false;
    _frameTimeout?: any;
    _loadRejector?: (error: any) => void;
    _loadResolver?: (res: any) => void;

    constructor(settings: Settings) {
        super(settings);

        this._el = settings.el;
        this._className = `${settings.className} ${styles.dmrFrame}`;
    }

    load(url: string) {
        return new Promise((resolve, reject) => {
            this._loadResolver = resolve;
            this._loadRejector = reject;

            this._frame = document.createElement('iframe');

            this._frame.className = 'dmr-frame ' + this._className;

            this._frame.src = url;

            this._frame.onload = this._onFrameLoad.bind(this);
            this._frame.onerror = this._onFrameError.bind(this);

            this._frameTimeout = setTimeout(
                this._onFrameTimeout.bind(this),
                DmrFrame.FRAME_FIMEOUT,
            );

            window.addEventListener('message', this._onMessage, false);

            this._el.appendChild(this._frame);
        });
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    _onMessage = (e) => {
        const isAllowedDomain = e.origin.match(
            /https:\/\/[^/]+\.(devmail|mail)\.ru(:\d+)?$/i,
        );
        if (!isAllowedDomain) {
            return;
        }

        console.error(e);

        const event = JsonSafe.parse(e.data) || {};

        if (!event.type || !event.action) {
            return;
        }

        const type = event.type;
        const action = event.action;

        if (!this._loaded) {
            this._loaded = true;
            clearTimeout(this._frameTimeout);

            this.emit(DmrFrame.EVENT_LOADING);

            if (type === 'billing' && action === 'payError') {
                this.emit(DmrFrame.EVENT_LOAD_ERROR);
                this._loadRejector &&
                    this._loadRejector(DmrFrame.EVENT_LOAD_ERROR);
            } else {
                this.emit(DmrFrame.EVENT_LOADED);
                this._loadResolver && this._loadResolver(DmrFrame.EVENT_LOADED);
            }
        }

        if (type === 'payment') {
            if (action === 'refreshWindow') {
                this.emit(DmrFrame.EVENT_RETRY);
            } else if (action === 'closeWindow') {
                this.emit(DmrFrame.EVENT_CLOSE);
            }
        } else if (type === 'billing') {
            if (action === 'paySuccess') {
                this.emit(DmrFrame.EVENT_PAYMENT_SUCCESS);
            } else if (action === 'payCancel') {
                this.emit(DmrFrame.EVENT_PAYMENT_CANCEL);
            } else if (action === 'payError') {
                this.emit(DmrFrame.EVENT_PAYMENT_FAIL);
            } else if (action === 'closeWindow') {
                this.emit(DmrFrame.EVENT_CLOSE);
            }
        }

        this.emit(DmrFrame.EVENT_MESSAGE, {
            type: event.type,
            action: event.action,
        });
    };

    _onFrameTimeout() {
        this._loadRejector &&
            this._loadRejector(DmrFrame.EVENT_LOAD_ERROR_TIMEOUT);

        this.emit(DmrFrame.EVENT_LOAD_ERROR_TIMEOUT);
        this.emit(DmrFrame.EVENT_LOAD_ERROR);
    }

    _onFrameLoad() {}

    _onFrameError() {
        clearTimeout(this._frameTimeout);

        this._loadRejector && this._loadRejector(DmrFrame.EVENT_LOAD_ERROR);

        this.emit(DmrFrame.EVENT_LOAD_ERROR);
    }

    _destroyFrame() {
        clearTimeout(this._frameTimeout);
        this._loadResolver = this._loadRejector = undefined;
        this._loaded = false;

        window.removeEventListener('message', this._onMessage, false);

        if (!this._frame) {
            return;
        }
        this._frame.src = 'about::blank';
        this._frame.onload = this._frame.onerror = null;
        this._frame.parentNode &&
            this._frame.parentNode.removeChild(this._frame);

        this._frame = undefined;
    }

    destroy() {
        if (this._frame) {
            this._destroyFrame();
        }

        this.off();
    }
}
